import { useRef } from 'react'
import { Transition } from 'react-transition-group'
import { CheckCircle2Icon, XCircleIcon, XIcon } from 'lucide-react'

import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type ToastVariant = 'success' | 'error'

export type ToastData = {
  id: number
  message: string
  variant: ToastVariant
}

type ToastProps = {
  toast: ToastData | null
  open: boolean
  onClose: () => void
}

const Toast = ({ toast, open, onClose }: ToastProps) => {
  const toastRef = useRef<HTMLDivElement>(null)
  const { strings } = useAppStore()

  return (
    <Transition in={open} nodeRef={toastRef} timeout={300} unmountOnExit>
      {(state) => {
        if (!toast) return null

        const isError = toast.variant === 'error'

        return (
          <div
            ref={toastRef}
            role={isError ? 'alert' : 'status'}
            aria-live={isError ? 'assertive' : 'polite'}
            aria-atomic="true"
            className={cn(
              'fixed bottom-[3rem] left-0 right-0 z-toast h-0',
              'flex items-end justify-center',
              'transition-all duration-300 ease-out-cubic',
              {
                'translate-y-0 opacity-100': state === 'entering' || state === 'entered',
                'translate-y-[1rem] opacity-0': state === 'exiting' || state === 'exited'
              }
            )}
          >
            <div
              className={cn(
                'whitespace-nowrap',
                'rounded-[1rem] border border-dark/20 bg-white px-[1.5rem] py-[1.5rem] text-dark shadow-lg',
                'flex items-center space-x-[1rem]'
              )}
            >
              {isError ? (
                <XCircleIcon className="shrink-0 text-red-500" size={18} />
              ) : (
                <CheckCircle2Icon className="shrink-0 text-green-600" size={18} />
              )}
              <Typography variant="small">{toast.message}</Typography>
              <button
                type="button"
                onClick={onClose}
                aria-label={strings?.labels?.close}
                className={cn(
                  'm-[-0.75rem] flex shrink-0 rounded-[0.5rem] p-[0.75rem] transition-colors hover:bg-light',
                  'focus-ring-dark'
                )}
              >
                <XIcon size={16} />
              </button>
            </div>
          </div>
        )
      }}
    </Transition>
  )
}

export default Toast
